import { presetProfile, selectedDailyPreset } from "./dailyConsumptionModel";

export const SEASONS = ["winter", "spring", "summer", "autumn"];
export const DAY_TYPES = ["weekdays", "saturdays", "sundays"];

export function legacyDayAllocation(demand, type) {
  const hours = DAY_TYPES.map((day) => {
    const block = demand.schedule?.[day] || {};
    const read = (v) => v == null || v === "" || v === "None" ? NaN : Number(String(v).split(":")[0]);
    const start = read(block.startHour), end = read(block.endHour);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start === end) return 0;
    return start < end ? end - start + 1 : 24 - start + end + 1;
  });
  if (hours.some((h) => h > 0)) return [{ season: "all", values: hours, hours: true }];
  const pairs = type === "Commercial" ? [[1.2, 1], [1.1, .9], [1.3, 1.1], [1.1, .9]]
    : type === "Residential Rural" ? [[1, 1], [.7, .5], [.6, .4], [.8, .6]]
      : [[.6, .4], [1, 1], [.8, .6], [.7, .3]];
  return SEASONS.map((season, i) => ({ season, values: [pairs[i][0], pairs[i][1], pairs[i][1]], hours: false }));
}

export function defaultDemandShape(type = "Commercial") {
  const commercial = type === "Commercial";
  const pairs = type === "Residential Rural"
    ? [[1, 1], [.7, .5], [.6, .4], [.8, .6]]
    : type === "Residential Urban"
      ? [[.6, .4], [1, 1], [.8, .6], [.7, .3]]
      : [[1, 1], [1, 1], [1, 1], [1, 1]];
  return {
    version: 1,
    dayMode: commercial ? "schedule" : "relative",
    useSchedule: commercial,
    seasonal: !commercial,
    dayWeights: Object.fromEntries(SEASONS.map((season, i) => [season, {
      weekdays: 100, saturdays: Number((100 * pairs[i][1] / pairs[i][0]).toFixed(6)),
      sundays: Number((100 * pairs[i][1] / pairs[i][0]).toFixed(6)),
    }])),
    dailyVariationPct: 10, hourlyVariationPct: 5,
    holidayMode: commercial ? "closed" : "normal", holidayPct: 50,
  };
}

export function isCustomDemandShape(shape, weights, type) {
  const preset = defaultDemandShape(type);
  if (!shape || shape.version !== 1) return false;
  const hasProfiles = shape.dayProfiles && Object.values(shape.dayProfiles).some(Array.isArray);
  if (hasProfiles && ["weekdays", "weekends"].some((day) =>
    shape.dayProfileCustom?.[day] || selectedDailyPreset(shape.dayProfiles[day]) !== (type === "Commercial" ? "daytime" : "morningEvening"))) return true;
  const keys = ["dayMode", "useSchedule", "seasonal", "dailyVariationPct", "hourlyVariationPct", "holidayMode", "holidayPct"];
  if (keys.some((key) => String(shape[key]) !== String(preset[key]))) return true;
  if (SEASONS.some((s) => DAY_TYPES.some((d) => Number(shape.dayWeights?.[s]?.[d]) !== preset.dayWeights[s][d]))) return true;
  if (hasProfiles) return false; // Explicit hourly profiles supersede legacy shares.
  const defaults = type === "Commercial" ? [5, 35, 45, 15] : [12.5, 25, 35, 27.5];
  const values = ["earlyMorning", "morning", "afternoon", "evening"].map((k) => weights?.[k]);
  if (values.every((v) => v == null || v === "")) return false;
  const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
  return total > 0 && values.some((v, i) => Math.abs((Number(v) || 0) / total * 100 - defaults[i]) > 1e-6);
}

// Only creation and an explicit preset application use these defaults. Never
// merge them into a loaded project: missing profiles retain the legacy curve.
export function initialDemandShape(type = "Commercial") {
  const key = type === "Commercial" ? "daytime" : "morningEvening";
  return {
    ...defaultDemandShape(type),
    dayProfileResolution: "hourly",
    dayProfiles: { weekdays: presetProfile(key), weekends: presetProfile(key) },
  };
}
